/**
 * Global handler: при готовности report_ready (notify-event с action=complete)
 * скачивает прикреплённый DOCX/PDF в браузер автоматически.
 *
 * Регистрируется один раз в Boot.jsx после accessToken.init(). Слушает все
 * notify-события (publisher=notify через ws.subscribe), фильтрует по
 * entitycode='report_ready' + actioncode='complete'. Для каждого готового
 * report_ready запрашивает /object/file (список) и инициирует download
 * первого подходящего бинарного файла (DOCX/PDF/XLSX/ZIP). HTML-файлы (legacy
 * preview) пропускаются — старый ReactToPrint-flow продолжает работать.
 */

import wsClient from "./websocket";
import rest from "./rest";
import config from "./config";

// Расширения, которые скачиваем автоматически. HTML — это legacy preview
// (печать через ReactToPrint), он остаётся в карточке отчёта.
const AUTO_DOWNLOAD_EXT = /\.(docx|pdf|xlsx|xls|doc|zip)$/i;

// Дедуп: один report_ready не должен скачиваться повторно. Notify-publisher
// может прислать complete-событие более одного раза (например при reconnect
// и повторной server-subscription с историей).
const downloadedReportIds = new Set();

// Пул report_ready, которых ждём именно мы — сюда ReportButton регистрирует
// id сразу после успешного /report/build. Notify-publisher шлёт complete-
// событие всем подписчикам сессии (включая чужие отчёты у админа, AOU не
// фильтрует pub/sub-канал). Скачиваем только то, что сами запустили —
// чужие events игнорируются естественным образом, без доп. REST-запросов.
const expectedReportIds = new Set();
function expect(reportReadyId) {
  if (reportReadyId) expectedReportIds.add(reportReadyId);
}

function pickFileToDownload(files) {
  if (!Array.isArray(files) || files.length === 0) return null;
  // Приоритет: .docx > .pdf > .xlsx > .doc
  const sorted = [...files].sort((a, b) => {
    const order = [".docx", ".pdf", ".xlsx", ".doc", ".zip"];
    const aIdx = order.findIndex((ext) => (a.name || "").toLowerCase().endsWith(ext));
    const bIdx = order.findIndex((ext) => (b.name || "").toLowerCase().endsWith(ext));
    if (aIdx === -1 && bIdx === -1) return 0;
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });
  return sorted.find((f) => AUTO_DOWNLOAD_EXT.test(f.name || "")) || null;
}

function triggerDownload(url, filename) {
  // <a download> в скрытом контейнере — единственный надёжный способ
  // инициировать скачивание из JS без window.open (popup blocker).
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "";
  a.rel = "noreferrer";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

async function handleReportReady(reportReadyId) {
  if (downloadedReportIds.has(reportReadyId)) {
    return;
  }
  // Ignored, если мы не подписывались на этот id (запустил кто-то другой,
  // notify-publisher всё равно прислал — это его типовое поведение).
  if (!expectedReportIds.has(reportReadyId)) {
    if (config.debug) {
      console.log("%c ReportDownload: skip " + reportReadyId + " — not expected", "color: gray");
    }
    return;
  }
  downloadedReportIds.add(reportReadyId);
  expectedReportIds.delete(reportReadyId);

  try {
    const files = await rest.q(
      "/object/file",
      { id: reportReadyId },
      { toArray: true }
    );
    const file = pickFileToDownload(files);
    if (!file) {
      // Только HTML/error — auto-download не нужен.
      if (config.debug) {
        console.log("%c ReportDownload: no binary file for report_ready " + reportReadyId, "color: gray");
      }
      return;
    }
    const base = (window.appConfig && window.appConfig.apiFilesDownloadPath) || "";
    const url = base + file.link;
    triggerDownload(url, file.name);
    if (config.debug) {
      console.log("%c ReportDownload: " + file.name + " ← " + reportReadyId, "color: green");
    }
  } catch (e) {
    downloadedReportIds.delete(reportReadyId);
    if (config.debug) {
      console.log("%c ReportDownload error: " + e.message, "color: red");
    }
  }
}

function isReportReadyComplete(notify) {
  // notify-payload (params.type='notify') содержит entitycode/classcode/
  // actioncode/object — фильтруем по entity=report_ready + action=complete.
  if (!notify || !notify.object) return false;
  const entity = notify.entitycode || notify.entity;
  const action = notify.actioncode || notify.action;
  return entity === "report_ready" && action === "complete";
}

let registered = false;

function register() {
  if (registered) return;
  registered = true;
  wsClient.subscribe("notify", (payload) => {
    if (isReportReadyComplete(payload)) {
      handleReportReady(payload.object);
    }
  });
}

const reportDownload = { register, expect };
export default reportDownload;
